import { BalanceData } from "../data/balance";
import { EndlessChestBand, Milestone, ModesData } from "../data/modes";
import { Fix64 } from "../maths/fix64";
import { LevelModifiers } from "../sim/modifiers";
import { STARS_PER_LEVEL, StarMask, starCount } from "../sim/stars";
import { utcDay } from "./orders";
import { trialWeek } from "./trials";

/**
 * Iron challenges and Endless mode as rules (docs/MODES.md): who may play them, the run modifiers that make the run,
 * what a win or a best pays, and the state the `modes` save section keeps. The waves are in sim/runModes.ts.
 */

// ------------------------------------------------------------------ Iron

/** A level's Iron challenge opens with all its stars (like Heroic). */
export function ironOpen(stars: StarMask): boolean { return starCount(stars) >= STARS_PER_LEVEL; }

/**
 * The level's Iron towers, as tower archetype indexes in draft order: the data's pick for the level (modes.json
 * `iron.towers`, written by tools/balance/modes.ts from the level's enemy mix and the towers owned at that level),
 * with any the player does not own replaced — and a level without a pick filled — from `fallbackOrder`. Always
 * `towersPerLevel` distinct owned towers when the player owns that many.
 */
export function ironTowers(balance: BalanceData, level: number, owned: readonly boolean[]): number[] {
  const d = balance.modes.iron;
  const index = (id: string) => balance.towers.findIndex((t) => t.id === id && !t.isBase);
  const out: number[] = [];
  const take = (i: number) => { if (i >= 0 && owned[i] === true && !out.includes(i) && out.length < d.towersPerLevel) out.push(i); };
  for (const id of d.towers.get(level) ?? []) take(index(id));
  for (const id of d.fallbackOrder) take(index(id));
  for (let i = 0; i < balance.towers.length; i++) if (!balance.towers[i].isBase) take(i);
  return out;
}

/** The Iron health multiplier of `level`: its calibrated own (modes.json `iron.levelHp`), else the default. */
export function ironHpMult(balance: BalanceData, level: number): Fix64 { return balance.modes.iron.levelHp.get(level) ?? balance.modes.iron.enemyHpMult; }

/** An Iron run's modifiers: the level's Iron health, the level's own elites, only these towers. */
export function ironModifiers(balance: BalanceData, towers: readonly number[], level: number): LevelModifiers {
  return { enemyHpMult: ironHpMult(balance, level), iron: { towers: [...towers] } };
}

/** Marks the first Iron win of a level pays (with its Iron star). */
export function ironMarks(data: ModesData, level: number): number { return data.iron.marksBase + data.iron.marksPerLevel * level; }
/** Marks a later Iron win of a level pays (at most repeatWinsPerDay a UTC day). */
export function ironRepeatMarks(data: ModesData, level: number): number { return data.iron.repeatMarksBase + data.iron.repeatMarksPerLevel * level; }

// ------------------------------------------------------------------ Endless

export function endlessUnlocked(data: ModesData, highestCleared: number): boolean { return highestCleared >= data.endless.unlockAfterLevel; }

/** An Endless run's modifiers: its enemy pool and power anchor are the player's progress when it starts. */
export function endlessModifiers(through: number): LevelModifiers {
  return { enemyHpMult: Fix64.One, endless: { through: Math.max(1, through) } };
}

/** The weekly chest band a best of `waves` reaches, or null under the first band. */
export function endlessChestBand(data: ModesData, waves: number): EndlessChestBand | null {
  let out: EndlessChestBand | null = null;
  for (const b of data.endless.weeklyChest) if (waves >= b.fromWave) out = b;
  return out;
}

/** An Endless score: waves cleared, then points. */
export interface EndlessScore { readonly waves: number; readonly points: number }
export function betterScore(a: EndlessScore, b: EndlessScore): boolean { return a.waves > b.waves || (a.waves === b.waves && a.points > b.points); }

// ------------------------------------------------------------------ state (save section `modes`)

export interface ModesStateJson {
  iron: { stars: number[]; day: number; tried: number[]; repeatDay: number; repeatPaid: number; milestones?: number[] };
  endless: {
    week: number; weekWaves: number; weekPoints: number; bestWaves: number; bestPoints: number; runs: number; pendingWeek: number; pendingWaves: number; lastRun: string;
    milestones?: MilestoneWeekJson[];
  };
}

/** One week's Endless milestones (gear v2): the week's best and the milestone indexes claimed. */
export interface MilestoneWeekJson { week: number; waves: number; claimed: number[] }

/** A free-route milestone as a claim list shows it (docs/GEAR_V2.md). */
export interface MilestoneView {
  /** Index in the data's list. */
  readonly index: number;
  /** The UTC week it belongs to (Endless), −1 for a one-off (Iron stars). */
  readonly week: number;
  readonly milestone: Milestone;
  /** The player's progress toward `milestone.count` (the week's best waves, the Iron stars). */
  readonly have: number;
  readonly reached: boolean;
  readonly claimed: boolean;
}

/** What a finished Iron run did to the state. */
export interface IronWin { readonly first: boolean; readonly repeatPaid: boolean }
/** What a finished Endless run did to the bests. */
export interface EndlessRecord { readonly counted: boolean; readonly newWeekBest: boolean; readonly newBest: boolean; readonly previousWeek: EndlessScore; readonly previousBest: EndlessScore }
/** A weekly chest waiting to be claimed: the week it was earned in and the best that earned it. */
export interface PendingChest { readonly week: number; readonly waves: number }

const isInt = (v: unknown): v is number => Number.isInteger(v);
const intList = (v: unknown): number[] => (Array.isArray(v) ? v.filter((x): x is number => isInt(x) && x >= 1) : []);

/**
 * Iron stars and the day's tries; Endless bests (this week and all time) and the weekly chest. Days and weeks are UTC
 * (the Daily Orders' and the Weekly Trial's clocks): a clock set back never re-opens a spent try.
 */
export class ModesState {
  private readonly ironStars: number[] = [];
  /** The UTC day `tried` belongs to. */
  private day = -1;
  private tried: number[] = [];
  private repeatDay = -1;
  private repeatPaid = 0;

  week = -1;
  weekWaves = 0;
  weekPoints = 0;
  bestWaves = 0;
  bestPoints = 0;
  runs = 0;
  private pendingWeek = -1;
  private pendingWaves = 0;
  private lastRun = "";
  /** Iron star milestones claimed (indexes). */
  private readonly ironClaimed: number[] = [];
  /** Endless milestone weeks still holding something: this week's, and a past week with a reached milestone unclaimed. */
  private msWeeks: MilestoneWeekJson[] = [];

  // ---- Iron
  hasIronStar(level: number): boolean { return this.ironStars.includes(level); }
  get ironStarCount(): number { return this.ironStars.length; }
  get ironStarLevels(): readonly number[] { return this.ironStars; }

  /** Whether today's try on `level` is spent. */
  triedToday(level: number, now: number): boolean { return utcDay(now) === this.day && this.tried.includes(level); }

  /** Spends today's try on `level` (an Iron run starts). False when it was spent already. */
  spendTry(level: number, now: number): boolean {
    const d = utcDay(now);
    if (d > this.day) { this.day = d; this.tried = []; }
    if (d < this.day || this.tried.includes(level)) return false;
    this.tried.push(level);
    return true;
  }

  /** An Iron win: the first on the level lights its star; a later one may pay the day's repeat Marks. */
  recordIronWin(level: number, now: number, repeatPerDay: number): IronWin {
    if (!isInt(level) || level < 1) return { first: false, repeatPaid: false };
    if (!this.hasIronStar(level)) {
      this.ironStars.push(level);
      this.ironStars.sort((a, b) => a - b);
      return { first: true, repeatPaid: false };
    }
    const d = utcDay(now);
    if (d > this.repeatDay) { this.repeatDay = d; this.repeatPaid = 0; }
    if (d < this.repeatDay || this.repeatPaid >= repeatPerDay) return { first: false, repeatPaid: false };
    this.repeatPaid++;
    return { first: false, repeatPaid: true };
  }

  /** Iron star milestones with their states. */
  ironMilestones(data: ModesData): MilestoneView[] {
    return data.iron.starMilestones.map((m, index) => ({ index, week: -1, milestone: m, have: this.ironStarCount, reached: this.ironStarCount >= m.count, claimed: this.ironClaimed.includes(index) }));
  }

  /** Claims an Iron star milestone (the caller pays it). A string says why not. */
  claimIronMilestone(data: ModesData, index: number): Milestone | string {
    const m = data.iron.starMilestones[index];
    if (m === undefined) return "no such milestone";
    if (this.ironClaimed.includes(index)) return "already claimed";
    if (this.ironStarCount < m.count) return "not reached";
    this.ironClaimed.push(index);
    return m;
  }

  // ---- Endless
  /** Endless weekly milestones with their states: this week's, and an earlier week's reached but unclaimed (oldest first). */
  endlessMilestones(data: ModesData, now: number): MilestoneView[] {
    this.rollWeek(data, now);
    const out: MilestoneView[] = [];
    for (const w of this.msWeeks) {
      data.endless.weeklyMilestones.forEach((m, index) => {
        const reached = w.waves >= m.count, claimed = w.claimed.includes(index);
        if (w.week === this.week || (reached && !claimed)) out.push({ index, week: w.week, milestone: m, have: w.waves, reached, claimed });
      });
    }
    return out;
  }

  /** Claims week `week`'s milestone `index` (the caller pays it). A string says why not. */
  claimEndlessMilestone(data: ModesData, now: number, week: number, index: number): Milestone | string {
    this.rollWeek(data, now);
    const m = data.endless.weeklyMilestones[index];
    const w = this.msWeeks.find((x) => x.week === week);
    if (m === undefined || w === undefined) return "no such milestone";
    if (w.claimed.includes(index)) return "already claimed";
    if (w.waves < m.count) return "not reached";
    w.claimed.push(index);
    return m;
  }

  private noteMilestoneWeek(data: ModesData): void {
    let w = this.msWeeks.find((x) => x.week === this.week);
    if (!w) { w = { week: this.week, waves: 0, claimed: [] }; this.msWeeks.push(w); }
    w.waves = Math.max(w.waves, this.weekWaves);
    // Keep this week and any earlier week with a reached milestone still unclaimed.
    this.msWeeks = this.msWeeks.filter((x) => x.week === this.week || data.endless.weeklyMilestones.some((m, i) => x.waves >= m.count && !x.claimed.includes(i)));
  }

  /**
   * Moves to `now`'s week: a week that ended with a best in a chest band leaves its chest pending (the better one if
   * one already waits), and this week's best starts again. Returns the pending chest, if any.
   */
  rollWeek(data: ModesData, now: number): PendingChest | null {
    const w = trialWeek(now);
    if (w > this.week) {
      if (this.week >= 0 && endlessChestBand(data, this.weekWaves) !== null && this.weekWaves > this.pendingWaves) { this.pendingWeek = this.week; this.pendingWaves = this.weekWaves; }
      this.week = w; this.weekWaves = 0; this.weekPoints = 0;
    }
    this.noteMilestoneWeek(data);
    return this.pending();
  }

  pending(): PendingChest | null { return this.pendingWeek >= 0 ? { week: this.pendingWeek, waves: this.pendingWaves } : null; }

  /** Takes the pending chest (the caller pays it). Null when none waits. */
  takePending(): PendingChest | null {
    const p = this.pending();
    this.pendingWeek = -1; this.pendingWaves = 0;
    return p;
  }

  /** A finished Endless run: the week's and the all-time bests. Counted once per `key` (a redrawn end screen adds nothing). */
  recordEndless(data: ModesData, now: number, key: string, score: EndlessScore): EndlessRecord {
    this.rollWeek(data, now);
    const previousWeek = { waves: this.weekWaves, points: this.weekPoints }, previousBest = { waves: this.bestWaves, points: this.bestPoints };
    if (key === this.lastRun) return { counted: false, newWeekBest: false, newBest: false, previousWeek, previousBest };
    this.lastRun = key;
    this.runs++;
    const newWeekBest = betterScore(score, previousWeek), newBest = betterScore(score, previousBest);
    if (newWeekBest) { this.weekWaves = score.waves; this.weekPoints = score.points; this.noteMilestoneWeek(data); }
    if (newBest) { this.bestWaves = score.waves; this.bestPoints = score.points; }
    return { counted: true, newWeekBest, newBest, previousWeek, previousBest };
  }

  toJson(): ModesStateJson {
    return {
      iron: { stars: [...this.ironStars], day: this.day, tried: [...this.tried], repeatDay: this.repeatDay, repeatPaid: this.repeatPaid, milestones: [...this.ironClaimed] },
      endless: {
        week: this.week, weekWaves: this.weekWaves, weekPoints: this.weekPoints, bestWaves: this.bestWaves, bestPoints: this.bestPoints, runs: this.runs, pendingWeek: this.pendingWeek, pendingWaves: this.pendingWaves, lastRun: this.lastRun,
        milestones: this.msWeeks.map((w) => ({ week: w.week, waves: w.waves, claimed: [...w.claimed] })),
      },
    };
  }

  static fromJson(j: unknown): ModesState {
    const s = new ModesState();
    if (typeof j !== "object" || j === null) return s;
    const src = j as Partial<ModesStateJson>;
    const i = (typeof src.iron === "object" && src.iron !== null ? src.iron : {}) as Partial<ModesStateJson["iron"]>;
    for (const l of intList(i.stars)) if (!s.ironStars.includes(l)) s.ironStars.push(l);
    s.ironStars.sort((a, b) => a - b);
    if (isInt(i.day)) s.day = i.day;
    s.tried = intList(i.tried);
    if (isInt(i.repeatDay)) s.repeatDay = i.repeatDay;
    if (isInt(i.repeatPaid) && i.repeatPaid >= 0) s.repeatPaid = i.repeatPaid;
    for (const m of Array.isArray(i.milestones) ? i.milestones.filter((x): x is number => isInt(x) && x >= 0) : []) if (!s.ironClaimed.includes(m)) s.ironClaimed.push(m);
    const e = (typeof src.endless === "object" && src.endless !== null ? src.endless : {}) as Partial<ModesStateJson["endless"]>;
    const nonNeg = (v: unknown) => (isInt(v) && v >= 0 ? v : 0);
    if (isInt(e.week)) s.week = e.week;
    s.weekWaves = nonNeg(e.weekWaves); s.weekPoints = nonNeg(e.weekPoints);
    s.bestWaves = Math.max(nonNeg(e.bestWaves), s.weekWaves); s.bestPoints = nonNeg(e.bestPoints);
    s.runs = nonNeg(e.runs);
    if (isInt(e.pendingWeek) && e.pendingWeek >= 0 && nonNeg(e.pendingWaves) > 0) { s.pendingWeek = e.pendingWeek; s.pendingWaves = nonNeg(e.pendingWaves); }
    if (typeof e.lastRun === "string") s.lastRun = e.lastRun;
    if (Array.isArray(e.milestones)) {
      for (const w of e.milestones) {
        if (typeof w !== "object" || w === null || !isInt(w.week) || w.week < 0) continue;
        s.msWeeks.push({ week: w.week, waves: nonNeg(w.waves), claimed: Array.isArray(w.claimed) ? w.claimed.filter((x): x is number => isInt(x) && x >= 0) : [] });
      }
    } else if (s.week >= 0 && s.weekWaves > 0) s.msWeeks.push({ week: s.week, waves: s.weekWaves, claimed: [] });
    return s;
  }
}
